import type { VideoRef } from '../components/VideoPlayer/EmbeddedVideo'

export type ResourceGroup = {
  title: string
  // date: publication date as dd/mm/yyyy, shown after the label
  links: { label: string; href: string; date?: string }[]
  // video embedded above the links
  video?: VideoRef
  // true when title and labels are translation keys rather than literal text
  i18n?: boolean
}

// Oleshky short documentary on Vimeo, falling back to the Google Drive copy when Vimeo won't allow
// embedding on this site. Also shown at the top of the Oleshky rally page.
export const OLESHKY_DOCUMENTARY: VideoRef = {
  host: 'vimeo',
  id: '1238705872',
  fallback: { host: 'drive', id: '1QF5OpHV7AzPNfODDbjmADQKd3MsUUZqg' },
}

// Oleshky / Drone Siege reporting. Shown on the left bank page and the Oleshky rally page.
export const oleshkyResources: ResourceGroup[] = [
  {
    i18n: true,
    title: 'leftBank.documentaryTitle',
    links: [],
    video: OLESHKY_DOCUMENTARY,
  },
  {
    title: 'New York Times',
    links: [
      {
        label: 'Trapped in a Kill Zone, a Ukrainian City Is Beginning to Starve',
        href: 'https://www.nytimes.com/2026/10/05/world/europe/oleshky-ukraine-russia-war-food.html',
        date: '05/10/2026',
      },
    ],
  },
  {
    title: 'Desk Russie',
    links: [
      {
        label: 'La ville prise en otage. Olechky l’insoumise, c’est l’Ukraine !',
        href: 'https://desk-russie.eu/2026/10/04/la-ville-prise-en-otage-olechky-linsoumise-cest-lukraine.html',
        date: '04/10/2026',
      },
    ],
  },
  {
    title: 'UK Government (OSCE)',
    links: [
      {
        label: 'Humanitarian situation in Oleshky: UK statement to the OSCE',
        href: 'https://www.gov.uk/government/speeches/humanitarian-situation-in-oleshky-uk-statement-to-the-osce',
        date: '02/10/2026',
      },
    ],
  },
  {
    title: 'CEPA',
    links: [
      {
        label: 'Russia: A Menu of Death in Ukraine’s Small Towns',
        href: 'https://cepa.org/article/russia-a-menu-of-death-in-ukraines-small-towns/',
        date: '01/10/2026',
      },
    ],
  },
  {
    title: 'France 2 / franceinfo',
    links: [
      {
        label:
          '"Il y a des gens qui meurent tout simplement de faim" : Olechky, une ville ukrainienne au bord de la famine',
        href: 'https://www.franceinfo.fr/monde/europe/manifestations-en-ukraine/il-y-a-des-gens-qui-meurent-tout-simplement-de-faim-oleshky-une-ville-ukrainienne-au-bord-de-la-famine_8216992.html',
        date: '30/09/2026',
      },
    ],
  },
  {
    title: 'Holodomor Museum',
    links: [
      {
        label:
          'Statement on the humanitarian situation in the temporarily occupied town of Oleshky',
        href: 'https://holodomormuseum.org.ua/en/novyny/statement-on-the-humanitarian-situation-in-the-temporarily-occupied-town-of-oleshky-and-adjacent-territorial-communities-in-kherson-region/',
        date: '15/09/2026',
      },
    ],
  },
  {
    title: 'BBC',
    links: [
      {
        label:
          'Eating garden weeds to survive in Russian-occupied city cut off from food and water',
        href: 'https://www.bbc.com/news/articles/c60qxywdx4vvo',
        date: '23/09/2026',
      },
    ],
  },
  {
    title: 'DW',
    links: [
      {
        label: 'Ukraine-Krieg: Warnung vor Hungersnot in Oleschky',
        href: 'https://www.dw.com/de/ukraine-krieg-warnung-vor-hungersnot-in-oleschky/a-79366794',
        date: '23/09/2026',
      },
    ],
  },
  {
    title: 'ABC News',
    links: [
      {
        label: "'The blockade is complete': Locals flee occupied south Ukraine as hunger deepens",
        href: 'https://abcnews.com/International/horror-movie-ukraines-oleshky-faces-catastrophe-russian-occupation/story?id=136685568',
        date: '25/09/2026',
      },
    ],
  },
  {
    title: 'Council of Europe Commissioner for Human Rights',
    links: [
      {
        label:
          'Ukraine: the rapidly deteriorating humanitarian crisis in Oleshky demands attention and action',
        href: 'https://www.coe.int/it/web/commissioner/-/ukraine-the-rapidly-deteriorating-humanitarian-crisis-in-oleshky-demands-attention-and-action',
      },
    ],
  },
  {
    i18n: true,
    title: 'leftBank.meidasTitle',
    links: [
      {
        label: 'leftBank.meidasVideo',
        href: 'https://www.youtube.com/watch?v=cl56_G0irSY',
        date: '26/06/2026',
      },
      {
        label: 'leftBank.meidasArticle',
        href: 'https://www.meidasplus.com/p/the-kherson-region-how-russia-uses',
        date: '02/07/2026',
      },
    ],
  },
  {
    i18n: true,
    title: 'leftBank.kyivPostTitle',
    links: [
      {
        label: 'leftBank.kyivPostArticle',
        href: 'https://www.kyivpost.com/post/79055',
        date: '26/06/2026',
      },
    ],
  },
  {
    title: 'Euromaidan Press',
    links: [
      {
        label:
          'Mined in, starved out, hunted from above—life in the towns Russia demands at the peace table',
        href: 'https://euromaidanpress.com/2026/03/16/mined-in-starved-out-hunted-from-above-life-in-the-towns-russia-demands-at-the-peace-table/',
        date: '16/03/2026',
      },
    ],
  },
  {
    title: 'Meidas Touch',
    links: [
      {
        label: 'Besieged Oleshki: Humanitarian Collapse Under Russian Control',
        href: 'https://www.meidasplus.com/p/besieged-oleshki-humanitarian-collapse',
        date: '26/03/2026',
      },
    ],
  },
  {
    title: 'Jake Broe Show',
    links: [
      {
        label: 'Zarina Zabrisky: The Russian Occupation Crisis | Jake Broe Podcast (E044)',
        href: 'https://www.youtube.com/watch?v=oJGCtjcU9iw',
        date: '11/04/2026',
      },
    ],
  },
  {
    title: 'Kyiv Independent',
    links: [
      {
        label:
          "Nearly 2,000 Ukrainian civilians trapped in Russian-occupied Kherson Oblast town facing 'humanitarian catastrophe'",
        href: 'https://kyivindependent.com/humanitarian-catastrophe-nearly-2-000-ukrainian-civilians-trapped-in-russian-occupied-oleshky-with-no-way-to-evacuate-little-food/',
        date: '15/04/2026',
      },
    ],
  },
  {
    title: 'BILD / Julian Röpcke',
    links: [
      {
        label: 'Putins Besatzer nehmen ganze Stadt als Geisel',
        href: 'https://www.bild.de/politik/ausland-und-internationales/putins-besatzer-nehmen-ganze-stadt-als-geisel-menschen-werden-zu-schutzschilden-69e233c8dc420b7f69a88bc8',
        date: '19/04/2026',
      },
    ],
  },
  {
    title: 'Deutsche Welle',
    links: [
      {
        label: 'Hunger in Oleshky: Ukraine asks for evacuation',
        href: 'https://www.dw.com/en/hunger-in-oleshky-ukraine-asks-for-evacuation/a-77395744',
        date: '06/06/2026',
      },
    ],
  },
  {
    title: 'The Times',
    links: [
      {
        label: "Four years of horror in Ukraine's ‘forgotten Bucha’",
        href: 'https://www.thetimes.com/world/russia-ukraine-war/article/four-years-horror-ukraine-war-oleshky-bucha-2wqk036cd',
        date: '18/06/2026',
      },
    ],
  },
]
